import { ChevronDown } from "lucide-react";
import moment from "moment";
import * as React from "react";

import { DropdownMenu } from "@/components/ui";
import { cn } from "@/lib/utils";

import { qaAuditItemLabel, QaAuditSelectItem } from "./qaStudio";

function triggerAuditDate(date?: string) {
  if (!date) return undefined;
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format("MMM D [at] h:mm a") : undefined;
}

function QaAuditResultIcon({ result }: { result: NonNullable<QaAuditSelectItem["result"]> }) {
  const failed = result === "REJECTED";
  return (
    <span
      className={cn(
        "flex size-3.5 shrink-0 items-center justify-center rounded-full",
        failed ? "bg-red-500" : "bg-emerald-500"
      )}
    >
      <svg viewBox="0 0 10 10" className="size-2" aria-hidden>
        {failed ? (
          <path d="M2 2 L8 8 M8 2 L2 8" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
        ) : (
          <path
            d="M1.5 5.2 L4 7.5 L8.5 2.5"
            fill="none"
            stroke="#fff"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </span>
  );
}

interface QaAuditPickerProps {
  items: QaAuditSelectItem[];
  value: string;
  onValueChange: (value: string) => void;
  themeClassName?: string;
}

export function QaAuditPicker({ items, value, onValueChange, themeClassName }: QaAuditPickerProps) {
  const selected = items.find(item => item.value === value) ?? items[0];
  if (!selected) return null;
  const selectedDate = triggerAuditDate(selected.createdDate);
  const canOpen = items.length > 1;
  const selectedLabel = qaAuditItemLabel(selected, items.length);

  const trigger = (
    <button
      type="button"
      disabled={!canOpen}
      className={cn(
        "inline-flex max-w-64 shrink-0 items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
        canOpen ? "cursor-pointer" : "cursor-default",
        selected.result === "REJECTED"
          ? "border-red-200 bg-red-50 text-red-800 hover:bg-red-100 dark:border-red-900 dark:bg-red-950/50 dark:text-red-200 dark:hover:bg-red-950/70"
          : selected.result === "APPROVED"
          ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200 dark:hover:bg-emerald-950/70"
          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      )}
    >
      {selected.result ? <QaAuditResultIcon result={selected.result} /> : null}
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold leading-5">{selectedLabel}</span>
        {selectedDate ? (
          <span className="block truncate text-[11px] font-medium leading-4 opacity-70">{selectedDate}</span>
        ) : null}
      </span>
      {canOpen ? <ChevronDown className="size-3.5 shrink-0 opacity-60" /> : null}
    </button>
  );

  if (!canOpen) return trigger;

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger render={trigger} />
      <DropdownMenu.Content align="end" className={cn("min-w-72", themeClassName)}>
        {items.map(item => {
          const date = triggerAuditDate(item.createdDate);
          return (
            <DropdownMenu.Item
              key={item.value}
              className={cn(
                "flex-col items-start gap-0.5",
                item.value === selected.value && "bg-slate-100 dark:bg-slate-800"
              )}
              onClick={() => onValueChange(item.value)}
            >
              <span className="flex items-center gap-2">
                {item.result ? <QaAuditResultIcon result={item.result} /> : null}
                <span className="min-w-0">
                  <span className="block text-sm font-semibold leading-5 text-slate-800 dark:text-slate-100">
                    {qaAuditItemLabel(item, items.length)}
                  </span>
                  {date ? (
                    <span className="block text-[11px] font-medium leading-4 text-slate-500 dark:text-slate-400">
                      {date}
                    </span>
                  ) : null}
                </span>
              </span>
            </DropdownMenu.Item>
          );
        })}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
